import { Link, Navigate, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { getTradeFair, TRADE_FAIRS } from "@/data/tradeFairs";
import { useSeoPageDraft } from "@/lib/useSeoPageDraft";

const TradeFairDetail = () => {
  const { slug } = useParams();
  const fair = getTradeFair(slug);
  const { open: openStartProject } = useStartProject();
  const draft = useSeoPageDraft(fair ? `/trade-fairs/${fair.slug}` : null);

  useSeo({
    title: fair ? fair.metaTitle : "Trade Fair — Asia Sourcing",
    description: fair ? fair.metaDescription : "",
    path: fair ? `/trade-fairs/${fair.slug}` : "/trade-fairs",
    type: "article",
    image: draft.heroUrl || undefined,
    jsonLd: fair && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${SITE_URL}/trade-fairs/${fair.slug}#article`,
          headline: fair.h1,
          description: fair.metaDescription,
          url: `${SITE_URL}/trade-fairs/${fair.slug}`,
          author: {
            "@type": "Organization",
            name: "Asia Sourcing India",
            url: SITE_URL,
          },
        },
        {
          "@type": "Event",
          "@id": `${SITE_URL}/trade-fairs/${fair.slug}#event`,
          name: fair.fullName,
          description: fair.lede,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: {
            "@type": "Place",
            name: fair.location,
            address: {
              "@type": "PostalAddress",
              addressLocality: fair.city,
              addressCountry: fair.country,
            },
          },
          organizer: { "@type": "Organization", name: fair.organizer, url: fair.officialUrl },
          url: fair.officialUrl,
        },
        {
          "@type": "FAQPage",
          mainEntity: fair.faqs.map(([question, answer]) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: { "@type": "Answer", text: answer },
          })),
        },
        breadcrumb([
          ["Home", SITE_URL],
          ["Trade fairs", `${SITE_URL}/trade-fairs`],
          [fair.name, `${SITE_URL}/trade-fairs/${fair.slug}`],
        ]),
      ],
    },
  });

  if (!fair) return <Navigate to="/trade-fairs" replace />;

  return (
    <article data-testid={`trade-fair-${fair.slug}`}>
      {draft.heroUrl && (
        <figure
          data-testid="trade-fair-hero-image"
          style={{
            margin: 0,
            width: "100%",
            maxHeight: "clamp(240px, 36vw, 440px)",
            overflow: "hidden",
            background: "var(--ink)",
          }}
        >
          <img
            src={draft.heroUrl}
            alt={draft.heroAlt || `${fair.fullName} — ${fair.city}, ${fair.country}`}
            loading="eager"
            fetchpriority="high"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          {draft.heroCaption && (
            <figcaption
              className="mono"
              style={{
                fontSize: ".72rem",
                letterSpacing: ".06em",
                color: "var(--ink-soft)",
                padding: ".5rem 1rem",
                background: "var(--paper-raised)",
                borderBottom: "1px solid var(--line)",
              }}
            >
              {draft.heroCaption}
            </figcaption>
          )}
        </figure>
      )}
      <section className="wrap" style={{ paddingBlock: "clamp(3rem, 5vw, 5rem)" }}>
        <Reveal as="p" className="eyebrow">
          Buyer guide · {fair.city}, {fair.country}
        </Reveal>
        <WordReveal
          as="h1"
          className="display"
          style={{
            fontSize: "clamp(1.8rem, 3.6vw, 3.1rem)",
            margin: "0 0 1.2rem",
            letterSpacing: "-.015em",
            lineHeight: 1.08,
          }}
          text={fair.h1}
        />
        <Reveal
          as="p"
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.08rem",
            lineHeight: 1.75,
            maxWidth: "72ch",
            margin: "0 0 2rem",
          }}
        >
          {fair.lede}
        </Reveal>

        {/* Key facts */}
        <div
          data-testid={`trade-fair-${fair.slug}-key-facts`}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0",
            border: "1px solid var(--line)",
            marginBottom: "2.4rem",
          }}
        >
          {[
            ["Location", fair.location],
            ["Organizer", fair.organizer],
            ["Cadence", fair.cadence],
            ["Categories", fair.categories.join(" · ")],
          ].map(([k, v], i) => (
            <div key={i} style={{ padding: "1rem 1.2rem", borderRight: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".6rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "0 0 .4rem",
                }}
              >
                {k}
              </p>
              <p style={{ fontSize: ".92rem", lineHeight: 1.5, margin: 0, color: "var(--ink)" }}>{v}</p>
            </div>
          ))}
        </div>

        {/* Two distinct CTAs — official event vs Asia Sourcing meeting */}
        <div
          data-testid={`trade-fair-${fair.slug}-ctas`}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1rem",
            marginBottom: "2.5rem",
          }}
        >
          <div style={{ padding: "1.3rem 1.4rem", border: "1px solid var(--line)", background: "var(--paper)" }}>
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".62rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .6rem" }}>
              Official event information
            </p>
            <p style={{ fontSize: ".95rem", lineHeight: 1.6, margin: "0 0 1rem", color: "var(--ink-soft)" }}>
              Registration, verified dates and exhibitor listings are on the official event website.
            </p>
            <a
              href={fair.officialUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="btn btn-ghost"
              data-testid={`trade-fair-${fair.slug}-official-link`}
            >
              Visit official event website →
            </a>
          </div>
          <div style={{ padding: "1.3rem 1.4rem", border: "1px solid var(--brass-deep)", background: "var(--paper)" }}>
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".62rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .6rem" }}>
              Meet the Asia Sourcing team
            </p>
            <p style={{ fontSize: ".95rem", lineHeight: 1.6, margin: "0 0 1rem", color: "var(--ink-soft)" }}>
              Attending {fair.name}? Share your sourcing requirements in advance and arrange a fair-week meeting.
            </p>
            <button
              type="button"
              onClick={openStartProject}
              className="btn btn-primary"
              data-testid={`trade-fair-${fair.slug}-schedule-meeting`}
            >
              Schedule a meeting
            </button>
          </div>
        </div>
      </section>

      {/* Body sections */}
      <section className="wrap" style={{ paddingBlock: "clamp(1rem, 2vw, 2rem)" }}>
        {fair.sections.map((s, i) => (
          <div
            key={i}
            style={{
              borderTop: "1px solid var(--line)",
              paddingBlock: "clamp(1.6rem, 3vw, 2.4rem)",
            }}
          >
            <Reveal
              as="h2"
              className="display"
              style={{
                fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)",
                margin: "0 0 1rem",
                letterSpacing: "-.01em",
              }}
            >
              {s.h2}
            </Reveal>
            {s.body.map((p, j) => (
              <Reveal
                as="p"
                key={j}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.02rem",
                  lineHeight: 1.75,
                  maxWidth: "72ch",
                  margin: "0 0 1rem",
                }}
              >
                {p}
              </Reveal>
            ))}
          </div>
        ))}
      </section>

      {/* Related trade fairs cross-link */}
      <section
        className="wrap"
        style={{ paddingBlock: "clamp(2.4rem, 5vw, 4rem)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
          Other international trade fairs
        </Reveal>
        <Reveal
          as="h2"
          className="display"
          style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)", margin: "0 0 1.4rem", letterSpacing: "-.01em" }}
        >
          Explore more buyer guides
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "1rem",
          }}
        >
          {TRADE_FAIRS.filter((f) => f.slug !== fair.slug).map((f) => (
            <Link
              key={f.slug}
              to={`/trade-fairs/${f.slug}`}
              data-testid={`trade-fair-${fair.slug}-related-${f.slug}`}
              style={{
                display: "block",
                padding: "1.1rem 1.2rem",
                border: "1px solid var(--line)",
                color: "var(--ink)",
                textDecoration: "none",
                background: "var(--paper)",
              }}
            >
              <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".6rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .4rem" }}>
                {f.city} · {f.country}
              </p>
              <p style={{ fontFamily: "var(--display)", fontSize: "1.02rem", margin: 0, lineHeight: 1.35 }}>
                {f.name} →
              </p>
            </Link>
          ))}
          <Link
            to="/trade-fairs"
            className="btn btn-ghost"
            data-testid={`trade-fair-${fair.slug}-all-fairs`}
            style={{ justifyContent: "flex-start" }}
          >
            All trade fairs →
          </Link>
        </div>
      </section>

      {/* Sourcing playbooks cross-link — every fair page connects to furniture + lighting pillars */}
      <section
        className="wrap"
        style={{ paddingBlock: "clamp(2.4rem, 5vw, 4rem)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
          India sourcing playbooks
        </Reveal>
        <Reveal
          as="h2"
          className="display"
          style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)", margin: "0 0 1.4rem", letterSpacing: "-.01em" }}
        >
          Continue your {fair.name} preparation
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: ".8rem",
          }}
        >
          {[
            { label: "Furniture sourcing from India →", to: "/categories/furniture" },
            { label: "Lighting sourcing from India →", to: "/categories/lighting" },
            { label: "Home décor sourcing from India →", to: "/categories/home-decor" },
            { label: "Jodhpur furniture cluster →", to: "/clusters/jodhpur" },
            { label: "Moradabad lighting cluster →", to: "/clusters/moradabad" },
            { label: "Supplier verification service →", to: "/services/supplier-verification-india" },
            { label: "Factory audit service →", to: "/services/factory-audit-india" },
            { label: "Quality inspection service →", to: "/services/quality-inspection-india" },
          ].map((l, i) => (
            <Link
              key={i}
              to={l.to}
              className="btn btn-ghost"
              data-testid={`trade-fair-${fair.slug}-related-service-${i}`}
              style={{ justifyContent: "flex-start" }}
            >
              {l.label}
            </Link>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section
        className="wrap"
        style={{ paddingBlock: "clamp(2.4rem, 5vw, 4rem)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
          {fair.name} · Frequently asked questions
        </Reveal>
        <div style={{ maxWidth: "76ch" }}>
          {fair.faqs.map(([q, a], i) => (
            <details
              key={i}
              data-testid={`trade-fair-${fair.slug}-faq-${i}`}
              style={{
                borderTop: "1px solid var(--line)",
                padding: "1.1rem 0",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontFamily: "var(--display)",
                  fontSize: "1.08rem",
                  lineHeight: 1.4,
                  color: "var(--ink)",
                }}
              >
                {q}
              </summary>
              <p
                style={{
                  marginTop: ".8rem",
                  color: "var(--ink-soft)",
                  fontSize: "1rem",
                  lineHeight: 1.75,
                }}
              >
                {a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(3rem, 5vw, 5rem)",
          textAlign: "left",
          borderTop: "1px solid var(--line)",
        }}
      >
        <WordReveal
          as="h2"
          className="display"
          style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.2rem)", margin: "0 0 1rem", letterSpacing: "-.01em", lineHeight: 1.12 }}
          text={`Planning to attend ${fair.name}? Talk to the Asia Sourcing team.`}
        />
        <Reveal
          as="p"
          style={{ color: "var(--ink-soft)", fontSize: "1.02rem", lineHeight: 1.7, maxWidth: "68ch", margin: "0 0 1.4rem" }}
        >
          Share your India sourcing requirements in advance and we will schedule a
          fair-week meeting or a follow-up India cluster visit.
        </Reveal>
        <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={openStartProject}
            className="btn btn-primary"
            data-testid={`trade-fair-${fair.slug}-final-cta`}
          >
            Schedule a meeting
          </button>
          <Link to="/contact" className="btn btn-ghost" data-testid={`trade-fair-${fair.slug}-contact-link`}>
            Contact us →
          </Link>
        </div>
      </section>
    </article>
  );
};

export default TradeFairDetail;
